import { MainApi } from 'api/MainApi'
import { LineString } from 'geojson'
import cloneDeep from 'lodash.clonedeep'
import SelectFileModal from 'pages/Mission/SelectFileModal/SelectFileModal'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MissionActions } from 'store/actions/mission'
import { selectMissionEditingTransfer, selectMissionInfo, selectMissionPointRoutes } from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import { missionPointsThunks } from 'store/thunks/missionPoints'
import { FILES_LOADING_TYPE, TRANSFER_CALCULATING_STATUSES, TRANSFER_TYPE } from 'utils/constants'

import CommonRouteItem from './CommonRouteItem/CommonRouteItem'

const MissionPointTransferItem = ({ type, hideActions }: { type: TRANSFER_TYPE; hideActions?: boolean }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [showSelectRecordedRouteModal, setShowSelectRecordedRouteModal] = useState(false)
  const missionPointRoutes = useSelector(selectMissionPointRoutes)
  const missionEditingTransfer = useSelector(selectMissionEditingTransfer)
  const missionInfo = useSelector(selectMissionInfo)
  const missionRoute = useMemo(() => {
    return type === TRANSFER_TYPE.START
      ? missionPointRoutes.find(route => route.properties.type === TRANSFER_TYPE.START)
      : missionPointRoutes.find(route => route.properties.type === TRANSFER_TYPE.FINISH)
  }, [missionPointRoutes, type])

  const onClick = () => {
    dispatch(MissionActions.setHighlightedFeature(missionRoute))
    missionRoute?.id && dispatch(MissionActions.setHighlightedFeatureInList(missionRoute.id))
  }

  const onEditClicked = () => {
    if (missionRoute) {
      store.dispatch(missionThunks.setEditingTransfer(missionRoute))
    }
  }

  const onRemoveClicked = () => {
    if (missionRoute?.properties.id) {
      store.dispatch(missionPointsThunks.deleteMissionTransfer(missionRoute?.properties.id))
    }
  }

  const onCancelEditingClicked = (e: any) => {
    e.stopPropagation()
    store.dispatch(missionThunks.cancelEditingTransfer())
  }

  const onSaveClicked = (e: any) => {
    e.stopPropagation()
    store.dispatch(missionPointsThunks.patchMissionTransfer())
  }

  const text = useMemo(() => {
    switch (type) {
      case TRANSFER_TYPE.START:
        return t('view_zones.start_transfer')
      case TRANSFER_TYPE.FINISH:
        return t('view_zones.finish_transfer')
    }
  }, [t, type])

  const editing = useMemo(() => {
    return missionEditingTransfer === missionRoute
  }, [missionEditingTransfer, missionRoute])

  const onCalculateClicked = async () => {
    try {
      if (missionRoute?.properties.id) {
        await MainApi.startCalculatingMissionPointRoute(missionRoute?.properties.id)
      }
    } catch (error) {
      console.log(error)
    }
  }

  async function onSelectRecordedRoute(fileId: number) {
    try {
      if (missionRoute?.properties.id) {
        await MainApi.patchMissionPointTransferRouteWithRecorded(missionRoute.properties.id, fileId)
        if (missionInfo?.id) {
          const mission = await MainApi.fetchMission(missionInfo.id)
          dispatch(MissionActions.setMission(mission))
        }
      }
      setShowSelectRecordedRouteModal(false)
    } catch (e) {
      console.error(e)
    }
  }

  return missionRoute?.id ? (
    <>
      <CommonRouteItem
        id={missionRoute.id}
        text={text}
        itemType={type}
        editing={editing}
        status={missionRoute.properties.status}
        length={missionRoute.properties.length}
        executionTime={missionRoute.properties.executionTime}
        onClick={onClick}
        onEdit={onEditClicked}
        onCancel={onCancelEditingClicked}
        onRemove={onRemoveClicked}
        onSave={onSaveClicked}
        hideActions={hideActions}
        onCalculate={onCalculateClicked}
        onUseRecordedRoute={() => setShowSelectRecordedRouteModal(true)}
      />
      {showSelectRecordedRouteModal && (
        <SelectFileModal
          isOpen={showSelectRecordedRouteModal}
          onClose={() => setShowSelectRecordedRouteModal(false)}
          onConfirmClick={(name, id) => id && onSelectRecordedRoute(id)}
          mode={FILES_LOADING_TYPE.ROUTE}
        />
      )}
    </>
  ) : null
}

export default MissionPointTransferItem
